import { css } from "@emotion/react";
import type { PropsWithChildren } from "react";
import { Suspense, useMemo } from "react";
import { Focusable } from "react-aria";
import { graphql, useLazyLoadQuery } from "react-relay";
import {
  Group,
  Panel,
  Separator,
  useDefaultLayout,
} from "react-resizable-panels";
import { useSearchParams } from "react-router";
import invariant from "tiny-invariant";

import {
  Flex,
  Icon,
  Icons,
  LinkButton,
  RichTooltip,
  Skeleton,
  Text,
  TooltipArrow,
  TooltipTrigger,
  View,
} from "@phoenix/components";
import { compactResizeHandleCSS } from "@phoenix/components/resize";
import { LatencyText } from "@phoenix/components/trace/LatencyText";
import { SpanStatusBadge } from "@phoenix/components/trace/SpanStatusBadge";
import { TokenCostsDetails } from "@phoenix/components/trace/TokenCostsDetails";
import { TraceTreeProvider } from "@phoenix/components/trace/TraceTree";
import { TraceTreeSkeleton } from "@phoenix/components/trace/TraceTreeSkeleton";
import { TraceTreeToolbar } from "@phoenix/components/trace/TraceTreeToolbar";
import type { SpanStatusCodeType } from "@phoenix/components/trace/types";
import { SELECTED_SPAN_NODE_ID_PARAM } from "@phoenix/constants/searchParams";
import { costFormatter } from "@phoenix/utils/numberFormatUtils";
import { clearSelectionScopedParams } from "@phoenix/utils/urlUtils";

import type {
  TraceDetailsQuery,
  TraceDetailsQuery$data,
} from "./__generated__/TraceDetailsQuery.graphql";
import { ConnectedTraceTree } from "./ConnectedTraceTree";
import { SpanDetails, SpanDetailsSkeleton } from "./SpanDetails";
import { SpanInfoCardsProvider } from "./SpanInfoCardsContext";
import { TraceHeaderTraceAnnotations } from "./TraceHeaderTraceAnnotations";

type RootSpan = NonNullable<
  TraceDetailsQuery$data["project"]["trace"]
>["rootSpans"]["edges"][number]["span"];

type CostSummary = NonNullable<
  TraceDetailsQuery$data["project"]["trace"]
>["costSummary"];

export type TraceDetailsProps = {
  traceId: string;
  projectId: string;
};

const traceDetailsCSS = css`
  flex: 1 1 auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const traceDetailsGroupCSS = css`
  flex: 1 1 auto;
  overflow: hidden;
`;

const TRACE_DETAILS_LAYOUT_ID = "trace-details-layout";

/**
 * A component that shows the details of a trace (e.g. a collection of spans).
 * The selected span lives in the `selectedSpanNodeId` URL search param so it
 * survives reloads and is shareable.
 */
export function TraceDetails({ traceId, projectId }: TraceDetailsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  return (
    <TraceDetailsView
      traceId={traceId}
      projectId={projectId}
      selectedSpanNodeId={searchParams.get(SELECTED_SPAN_NODE_ID_PARAM)}
      onSpanSelectionChange={(spanNodeId) => {
        setSearchParams(
          (searchParams) => {
            searchParams.set(SELECTED_SPAN_NODE_ID_PARAM, spanNodeId);
            return searchParams;
          },
          { replace: true }
        );
      }}
    />
  );
}

export type TraceDetailsViewProps = TraceDetailsProps & {
  /** The Relay node ID of the span to show, or null to show the root span */
  selectedSpanNodeId: string | null;
  /** Called with the Relay node ID of the span picked in the trace tree */
  onSpanSelectionChange: (spanNodeId: string) => void;
  /**
   * The storage key under which the tree/details split is remembered. Views
   * that mount several traces at once give each its own so they do not
   * overwrite one another.
   * @default "trace-details-layout"
   */
  layoutId?: string;
};

/**
 * The trace details with the span selection controlled by the caller, for
 * views that own the selection themselves (such as the compare view).
 */
export function TraceDetailsView({
  traceId,
  projectId,
  selectedSpanNodeId: requestedSpanNodeId,
  onSpanSelectionChange,
  layoutId = TRACE_DETAILS_LAYOUT_ID,
}: TraceDetailsViewProps) {
  const data = useLazyLoadQuery<TraceDetailsQuery>(
    graphql`
      query TraceDetailsQuery($traceId: ID!, $id: ID!) {
        project: node(id: $id) {
          ... on Project {
            trace(traceId: $traceId) {
              id
              projectSessionId
              ...ConnectedTraceTree
              rootSpans: spans(
                first: 1
                filterCondition: "parent_span is None"
              ) {
                edges {
                  span: node {
                    statusCode
                    id
                    spanId
                    parentId
                  }
                }
              }
              latencyMs
              costSummary {
                prompt {
                  cost
                }
                completion {
                  cost
                }
                total {
                  cost
                }
              }
            }
          }
        }
      }
    `,
    { traceId: traceId as string, id: projectId as string },
    {
      fetchPolicy: "store-and-network",
    }
  );
  invariant(data.project.trace, "Trace is required to view the trace details");
  const traceLatencyMs =
    data.project.trace?.latencyMs != null ? data.project.trace.latencyMs : null;
  const costSummary = data?.project?.trace?.costSummary;
  const rootSpans: RootSpan[] = useMemo(() => {
    const gqlSpans = data.project.trace?.rootSpans.edges || [];
    return gqlSpans.map((node) => node.span);
  }, [data]);
  invariant(rootSpans.length > 0, "At least one root must be resolvable");
  const rootSpan = rootSpans[0];
  const selectedSpanNodeId = requestedSpanNodeId ?? rootSpan.id;

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: layoutId,
    storage: localStorage,
  });

  return (
    <main css={traceDetailsCSS}>
      <TraceHeader
        projectId={projectId}
        traceNodeId={data.project.trace.id}
        rootSpan={rootSpan}
        latencyMs={traceLatencyMs}
        costSummary={costSummary}
        sessionId={data.project.trace?.projectSessionId}
      />
      <Group
        orientation="horizontal"
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
        css={traceDetailsGroupCSS}
      >
        <Panel id="trace-tree" defaultSize="30%" minSize="5%">
          <TraceTreeProvider>
            <ScrollingPanelContent>
              <TraceTreeToolbar />
              <ConnectedTraceTree
                trace={data.project.trace}
                selectedSpanNodeId={selectedSpanNodeId}
                onSpanClick={(span) => {
                  onSpanSelectionChange(span.id);
                }}
              />
            </ScrollingPanelContent>
          </TraceTreeProvider>
        </Panel>
        <Separator css={compactResizeHandleCSS} />
        <Panel id="span-details">
          {/* above the span details, so a collapse the reader asked for holds
              as they move between spans in the tree */}
          <SpanInfoCardsProvider>
            <ScrollingTabsWrapper>
              {selectedSpanNodeId ? (
                <Suspense fallback={<SpanDetailsSkeleton />}>
                  <SpanDetails spanNodeId={selectedSpanNodeId} />
                </Suspense>
              ) : null}
            </ScrollingTabsWrapper>
          </SpanInfoCardsProvider>
        </Panel>
      </Group>
    </main>
  );
}

/**
 * A loading placeholder for `TraceDetails`: the trace header's metrics above
 * the trace tree and the span details, split as the loaded trace is.
 */
export function TraceDetailsSkeleton({
  layoutId = TRACE_DETAILS_LAYOUT_ID,
}: Pick<TraceDetailsViewProps, "layoutId">) {
  const { defaultLayout } = useDefaultLayout({
    id: layoutId,
    storage: localStorage,
  });
  return (
    <main css={traceDetailsCSS} aria-busy="true">
      <View paddingTop="size-100" paddingBottom="size-150" paddingX="size-200">
        <Skeleton height="calc(var(--global-line-height-s) + var(--global-line-height-l))" />
      </View>
      <Group
        orientation="horizontal"
        defaultLayout={defaultLayout}
        css={traceDetailsGroupCSS}
      >
        <Panel id="trace-tree" defaultSize="30%" minSize="5%">
          <ScrollingPanelContent>
            <View padding="size-100" flex="none">
              <Skeleton height="var(--global-input-height-m)" />
            </View>
            <TraceTreeSkeleton />
          </ScrollingPanelContent>
        </Panel>
        <Panel id="span-details">
          <SpanDetailsSkeleton />
        </Panel>
      </Group>
    </main>
  );
}

function TraceHeader({
  rootSpan,
  traceNodeId,
  latencyMs,
  costSummary,
  sessionId,
  projectId,
}: {
  rootSpan: RootSpan | null;
  traceNodeId: string;
  latencyMs: number | null;
  costSummary?: CostSummary | null;
  sessionId?: string | null;
  projectId: string;
}) {
  const [searchParams] = useSearchParams();
  const statusCode = (rootSpan?.statusCode ?? "UNSET") as SpanStatusCodeType;
  const sessionSearch = clearSelectionScopedParams(searchParams);
  return (
    <View
      paddingTop="size-100"
      paddingBottom="size-150"
      paddingX="size-200"
      borderBottomWidth="thin"
      borderBottomColor="default"
    >
      <Flex
        direction="row"
        gap="size-400"
        alignItems="start"
        css={css`
          box-sizing: content-box;
        `}
      >
        <Flex
          direction="column"
          alignItems="start"
          css={css`
            align-self: stretch;
          `}
        >
          <Text elementType="h3" size="S" color="text-700">
            Status
          </Text>
          <div
            css={css`
              flex: 1 1 auto;
              display: flex;
              align-items: center;
            `}
          >
            <SpanStatusBadge statusCode={statusCode} labelVariant="full" />
          </div>
        </Flex>
        <Flex direction="column">
          <Text elementType="h3" size="S" color="text-700">
            Total Cost
          </Text>
          <TooltipTrigger
            delay={0}
            isDisabled={(costSummary?.total?.cost ?? 0) === 0}
          >
            <Focusable>
              <Text size="L" role="button">
                {costFormatter(costSummary?.total?.cost ?? 0)}
              </Text>
            </Focusable>
            <RichTooltip placement="bottom">
              <TooltipArrow />
              <View width="size-3600">
                <TokenCostsDetails
                  total={costSummary?.total?.cost ?? 0}
                  prompt={costSummary?.prompt?.cost ?? 0}
                  completion={costSummary?.completion?.cost ?? 0}
                />
              </View>
            </RichTooltip>
          </TooltipTrigger>
        </Flex>
        <Flex direction="column">
          <Text elementType="h3" size="S" color="text-700">
            Latency
          </Text>
          {typeof latencyMs === "number" ? (
            <LatencyText latencyMs={latencyMs} size="L" />
          ) : (
            <Text size="L">--</Text>
          )}
        </Flex>
        <Suspense fallback={null}>
          <Flex direction="row" alignItems="stretch" alignSelf="stretch">
            <TraceHeaderTraceAnnotations traceId={traceNodeId} />
          </Flex>
        </Suspense>
        {sessionId && (
          <span
            css={css`
              align-self: center;
              margin-left: auto;
            `}
          >
            <LinkButton
              size="S"
              variant="primary"
              leadingVisual={<Icon svg={<Icons.MessagesSquare />} />}
              to={{
                pathname: `/projects/${projectId}/sessions/${sessionId}`,
                search: sessionSearch,
              }}
            >
              View Session
            </LinkButton>
          </span>
        )}
      </Flex>
    </View>
  );
}

function ScrollingTabsWrapper({ children }: PropsWithChildren) {
  return (
    <div
      data-testid="scrolling-tabs-wrapper"
      css={css`
        height: 100%;
        overflow: hidden;
        .tabs {
          height: 100%;
          overflow: hidden;
          .tabs__extra {
            width: 100%;
            padding-right: var(--global-dimension-size-200);
            padding-bottom: var(--global-dimension-size-50);
          }
          .tabs__pane-container {
            min-height: 100%;
            height: 100%;
            overflow-y: auto;
            div[role="tabpanel"] {
              height: 100%;
            }
          }
        }
      `}
    >
      {children}
    </div>
  );
}

function ScrollingPanelContent({ children }: PropsWithChildren) {
  return (
    <div
      data-testid="scrolling-panel-content"
      css={css`
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      `}
    >
      {children}
    </div>
  );
}
